import { CopyableEmail } from "@/components/common/CopyableEmail";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  ExternalLink,
  FileText,
  Loader2,
  MoreVertical,
  SquarePen,
  UserRound,
} from "lucide-react";
import {
  AgentAssociationDisplay,
  DocumentTypeIcon,
  FolderBadge,
} from "@/components/knowledge-base";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRole } from "@/hooks/auth";
import { useDocumentParsingStatus } from "@/hooks/documents/useDocumentParsingStatus";
import { DocumentService } from "@/services/api/DocumentService";
import { PDFService } from "@/services/knowledgebase/PDFService";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

interface OutsideWorkspaceDocumentsSectionProps {
  isLoading: boolean;
  documents: DocumentWithMetadata[];
  openDocumentDetails: (doc: DocumentWithMetadata) => void;
}

const OutsideWorkspaceDocumentsSection = ({ isLoading, documents, openDocumentDetails }: OutsideWorkspaceDocumentsSectionProps) => {
  const { isInternalAdmin } = useRole();
  const { documentParsingStatus, processingDocuments } = useDocumentParsingStatus(documents);

  const handleViewDocument = async (document: DocumentWithMetadata) => {
    if (document.source_path) {
      const signedUrl = await DocumentService.getSignedUrl(document.source_path);
      if (signedUrl) window.open(signedUrl, "_blank");
    }
  };

  const getParsingStatusIndicator = (document: DocumentWithMetadata) => {
    const isPdf = PDFService.isPdf(document);
    const status = documentParsingStatus[document.id];

    if (isPdf) {
      if (!status || status.isCompleted) return null;
      if (status.isProcessing) {
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4 text-genie-secondary" />
                  <span className="text-xs text-genie-secondary">{status.progress}%</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>PDF is still being parsed. Please check back later.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }
      return null;
    }

    const documentStatus = document.status || document.metadata?.status;
    if (
      documentStatus === "in_progress" ||
      documentStatus === "processing" ||
      documentStatus === "pending"
    ) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1">
                <Loader2 className="h-4 w-4 text-genie-secondary animate-spin" />
                <span className="text-xs text-genie-secondary">Parsing...</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Document is being parsed. Please check back later.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return null;
  };

  const getDocumentStatusIcon = (document: DocumentWithMetadata) => {
    const docStatus = document.status || document.metadata?.status;
    const iconMap = {
      pending: { Icon: Loader2, color: "text-yellow-600", label: "Pending" },
      processing: { Icon: Loader2, color: "text-yellow-600", label: "Processing" },
      in_progress: { Icon: Loader2, color: "text-yellow-600", label: "Processing" },
      completed: { Icon: CheckCircle, color: "text-green-600", label: "Completed" },
      failed: { Icon: AlertCircle, color: "text-red-600", label: "Failed" },
    };
    const config = iconMap[docStatus || ""] || {
      Icon: AlertCircle,
      color: "text-gray-600",
      label: "Unknown",
    };
    return {
      Icon: config.Icon,
      color: config.color,
      label: config.label,
      isAnimated: docStatus === "processing" || docStatus === "in_progress",
    };
  };

  if (!isLoading && documents.length === 0) {
    return null;
  }

  return (
    <div className="bg-white p-4 sm:p-5 rounded-xl border border-genie-border mt-6">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-genie-text">Documents Attached to Your Genies</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Linked to your genies but owned by someone else — often an admin uploaded them on your behalf.
        </p>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 text-genie-primary animate-spin mr-2" />
          <span>Loading documents...</span>
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="grid gap-1.5">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className={`flex flex-col sm:flex-row items-start sm:items-center justify-between px-3 py-2 rounded-lg border transition-all duration-200 cursor-pointer w-full overflow-hidden ${
                  doc.el_doc_id
                    ? "bg-genie-primary/5 border-genie-primary/20 hover:shadow-sm hover:border-genie-primary/30"
                    : "bg-white border-genie-border hover:shadow-sm hover:border-genie-secondary/30"
                }`}
                onClick={() => openDocumentDetails(doc)}
              >
                <div className="flex items-center flex-1 min-w-0">
                  <div className="mr-2.5 shrink-0">
                    <DocumentTypeIcon document={doc} iconSize="md" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
                      <h3 className="font-medium text-sm text-genie-text truncate pr-1">
                        {doc.name}
                      </h3>
                      {(() => {
                        const { Icon, color, label, isAnimated } = getDocumentStatusIcon(doc);
                        return (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Icon
                                  className={`h-4 w-4 ${color} ${isAnimated ? "animate-spin" : ""}`}
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{label}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })()}
                      <span className="text-xs text-gray-400">
                        {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : "N/A"}
                      </span>
                      {doc.el_doc_id ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-genie-primary/10 text-genie-primary cursor-help">
                                <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                                In KB
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>In Knowledge Base</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-genie-secondary/10 text-genie-secondary cursor-help">
                                <AlertCircle className="h-2.5 w-2.5 mr-0.5" />
                                Not In KB
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Not in Knowledge Base</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}

                      {(doc.owner?.full_name || doc.owner?.email) && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
                          <UserRound className="h-2.5 w-2.5" />
                          <CopyableEmail email={doc.owner.full_name || doc.owner.email} />
                        </span>
                      )}

                      <FolderBadge folderId={doc.folder_id} size="sm" />
                    </div>
                    {doc.metadata?.description && (
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                        {doc.metadata.description}
                      </p>
                    )}
                  </div>
                  <div className="ml-auto sm:hidden shrink-0" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-5 w-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {doc.source_path && (
                          <DropdownMenuItem onClick={() => handleViewDocument(doc)}>
                            <ExternalLink className="h-4 w-4 mr-2" />
                            View source
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => openDocumentDetails(doc)}>
                          View details
                        </DropdownMenuItem>
                        {doc.el_doc_id && isInternalAdmin() && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => {
                                window.location.href = `/admin/knowledge-base?document=${doc.el_doc_id}`;
                              }}
                            >
                              Open in KB
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <div
                  className="hidden sm:flex items-center gap-1.5 mt-2 sm:mt-0 shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="ml-1">
                    <AgentAssociationDisplay
                      dependentAgents={doc.dependent_agents || []}
                      size="lg"
                      maxDisplay={5}
                      mode="avatars"
                      showLabel={false}
                    />
                  </div>
                  {getParsingStatusIndicator(doc)}
                  {doc.parsed_path && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-genie-primary hover:text-genie-primary-hover hover:bg-genie-primary-light"
                            onClick={() => handleViewDocument(doc)}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>View source file</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-gray-400 hover:text-genie-primary hover:bg-genie-primary-light"
                          onClick={() => openDocumentDetails(doc)}
                        >
                          <SquarePen className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>View details</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {doc.el_doc_id && isInternalAdmin() && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-genie-primary hover:text-genie-primary/80"
                      onClick={() => {
                        window.location.href = `/admin/knowledge-base?document=${doc.el_doc_id}`;
                      }}
                      title="View this document in the Knowledge Base"
                    >
                      Open in KB
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OutsideWorkspaceDocumentsSection;
